import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendChatMessage, getChatHistory, clearChatHistory } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Minimize2, 
  Maximize2,
  Trash2,
  Ship,
  Sparkles,
  Bot,
  User,
  ExternalLink,
  BarChart3,
  AlertCircle,
  Clock,
  Package,
  Search
} from 'lucide-react';

export function ChatBot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [actions, setActions] = useState([]); // Store actions from last response
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Load chat history when opening
  useEffect(() => {
    if (isOpen && sessionId) {
      loadHistory();
    }
  }, [isOpen, sessionId]);

  const loadHistory = async () => {
    try {
      const res = await getChatHistory(sessionId, 10);
      if (res.data && res.data.length > 0) {
        const historyMessages = res.data.reverse().flatMap(h => [
          { role: 'user', content: h.user_message },
          { role: 'assistant', content: h.ai_response }
        ]);
        setMessages(historyMessages);
      }
    } catch (err) {
      console.error('Error loading chat history:', err);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    setIsLoading(true);
    try {
      const res = await sendChatMessage(userMessage, sessionId);
      
      // Store session ID
      if (res.data.session_id && !sessionId) {
        setSessionId(res.data.session_id);
      }
      
      // Store actions if present
      if (res.data.actions && res.data.actions.length > 0) {
        setActions(res.data.actions);
      } else {
        setActions([]);
      }
      
      // Add AI response with actions
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        actions: res.data.actions || []
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearHistory = async () => {
    if (!sessionId) {
      setMessages([]);
      setActions([]);
      return;
    }
    
    try {
      await clearChatHistory(sessionId);
      setMessages([]);
      setActions([]);
      setSessionId(null);
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  };

  const handleActionClick = (action) => {
    if (action.type === 'navigate' && action.url) {
      // Close chatbot and navigate
      setIsOpen(false);
      navigate(action.url);
    } else if (action.type === 'suggest' && action.query) {
      // Suggestion chip — send the suggested query as a new user message
      sendDirectMessage(action.query);
    }
  };

  // Direct send function for quick actions (bypasses state delay)
  const sendDirectMessage = async (query) => {
    if (isLoading) return;
    
    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: query }]);
    
    setIsLoading(true);
    try {
      const res = await sendChatMessage(query, sessionId);
      
      // Store session ID
      if (res.data.session_id && !sessionId) {
        setSessionId(res.data.session_id);
      }
      
      // Store actions if present
      if (res.data.actions && res.data.actions.length > 0) {
        setActions(res.data.actions);
      } else {
        setActions([]);
      }
      
      // Add AI response with actions
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        actions: res.data.actions || []
      }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Lists
        if (line.startsWith('- ')) {
          return `<li class="ml-4">${line.substring(2)}</li>`;
        }
        return line;
      })
      .join('<br />');
  };

  // Quick action buttons - Enhanced with more options
  const quickActions = [
    { label: '📊 Resumen', query: 'Dame un resumen de estadísticas', icon: BarChart3 },
    { label: '🔴 Semáforo Rojo', query: '¿Qué contenedores tenemos en semáforo rojo?', icon: AlertCircle },
    { label: '🚢 En Puerto', query: '¿Qué contenedores hay en puerto?', icon: Ship },
    { label: '📅 Esta Semana', query: '¿Qué embarques llegan esta semana?', icon: Clock },
    { label: '📋 Pendientes', query: 'Embarques pendientes', icon: Package },
  ];

  // Small reusable chip the user can tap to send an example query. `tiny`
  // variant is used in the persistent row above the input (smaller padding).
  const ExampleChip = ({ text, hint, onClick, tiny }) => (
    <button
      onClick={() => onClick(text)}
      className={`${tiny ? 'px-2 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-primary/40 rounded-full transition-colors inline-flex items-center gap-1`}
      data-testid={`chat-example-${text.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
    >
      <span className="font-mono">{text}</span>
      {hint && <span className="text-slate-500 text-[10px]">· {hint}</span>}
    </button>
  );

  return (
    <>
      {/* Chat Toggle Button - positioned to avoid overlap with Emergent badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-24 z-50 p-4 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-600' 
            : 'bg-primary hover:bg-primary/90'
        }`}
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Bot className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 transition-all duration-300 ${
            isMinimized 
              ? 'bottom-6 right-36 w-64' 
              : 'bottom-6 right-24 w-96 sm:w-[420px]'
          }`}
          data-testid="chatbot-window"
        >
          <div className={`bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden ${
            isMinimized ? '' : 'h-[500px] sm:h-[550px]'
          }`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/20 to-purple-500/20 border-b border-slate-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-primary/30 rounded-xl flex items-center justify-center">
                      <Bot className="w-5 h-5 text-primary" />
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900"></span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-white">OFIMEX Assistant</h3>
                    <p className="text-xs text-slate-400">Consulta tus embarques</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 && (
                    <button
                      onClick={handleClearHistory}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                      title="Limpiar historial"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages Area */}
                <div className="h-[340px] sm:h-[380px] overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-4">
                      <div className="w-14 h-14 mx-auto mb-3 bg-primary/20 rounded-2xl flex items-center justify-center">
                        <Ship className="w-7 h-7 text-primary" />
                      </div>
                      <h4 className="text-white font-medium mb-1">¡Hola! 👋</h4>
                      <p className="text-sm text-slate-400 mb-4">
                        Te guío por las consultas más comunes. Toca un botón o escribe tu pregunta.
                      </p>

                      {/* Categorized examples — gives the user a clear menu of
                          what the bot can do, organized by intent group. */}
                      <div className="text-left space-y-3 mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">🔎 Buscar algo específico</p>
                          <div className="flex flex-wrap gap-1.5">
                            <ExampleChip onClick={sendDirectMessage} text="MEDUWX474325" hint="MBL" />
                            <ExampleChip onClick={sendDirectMessage} text="CAIU8564842" hint="contenedor" />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">📊 Estado del sistema</p>
                          <div className="flex flex-wrap gap-1.5">
                            <ExampleChip onClick={sendDirectMessage} text="Resumen" />
                            <ExampleChip onClick={sendDirectMessage} text="¿Qué hay en puerto?" />
                            <ExampleChip onClick={sendDirectMessage} text="Semáforo rojo" />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">📅 Próximas y pendientes</p>
                          <div className="flex flex-wrap gap-1.5">
                            <ExampleChip onClick={sendDirectMessage} text="¿Qué llega esta semana?" />
                            <ExampleChip onClick={sendDirectMessage} text="Pendientes" />
                            <ExampleChip onClick={sendDirectMessage} text="Promedio de días en puerto" />
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => sendDirectMessage('ayuda')}
                        className="text-xs text-primary hover:underline"
                        data-testid="chatbot-show-help"
                      >
                        Ver todas las opciones →
                      </button>
                    </div>
                  ) : (
                    messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                          <div className="flex items-end gap-2">
                            {msg.role === 'assistant' && (
                              <div className="w-6 h-6 bg-primary/30 rounded-lg flex-shrink-0 flex items-center justify-center">
                                <Sparkles size={12} className="text-primary" />
                              </div>
                            )}
                            <div>
                              <div
                                className={`px-4 py-3 rounded-2xl ${
                                  msg.role === 'user'
                                    ? 'bg-primary text-white rounded-br-md'
                                    : msg.isError
                                      ? 'bg-red-500/20 text-red-200 border border-red-500/30'
                                      : 'bg-slate-800 text-slate-200 rounded-bl-md'
                                }`}
                              >
                                <div 
                                  className="text-sm whitespace-pre-wrap"
                                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                />
                              </div>
                              
                              {/* Action buttons */}
                              {msg.actions && msg.actions.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {msg.actions.map((action, actionIdx) => {
                                    const isSuggest = action.type === 'suggest';
                                    return (
                                      <button
                                        key={actionIdx}
                                        onClick={() => handleActionClick(action)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${
                                          isSuggest
                                            ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-600'
                                            : 'bg-primary/20 hover:bg-primary/30 text-primary border-primary/30'
                                        }`}
                                        data-testid={isSuggest ? `chat-suggest-${actionIdx}` : `chat-nav-${actionIdx}`}
                                      >
                                        {!isSuggest && <ExternalLink size={12} />}
                                        {action.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {msg.role === 'user' && (
                              <div className="w-6 h-6 bg-slate-700 rounded-lg flex-shrink-0 flex items-center justify-center">
                                <User size={12} className="text-slate-300" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="flex items-end gap-2">
                        <div className="w-6 h-6 bg-primary/30 rounded-lg flex items-center justify-center">
                          <Sparkles size={12} className="text-primary" />
                        </div>
                        <div className="bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-md">
                          <div className="flex items-center gap-2 text-slate-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Pensando...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-slate-700 p-4 bg-slate-800/50">
                  {/* Persistent suggestion row — always available so user
                      knows what they can ask, even mid-conversation. */}
                  {messages.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="chatbot-persistent-suggestions">
                      <ExampleChip onClick={sendDirectMessage} text="Resumen" tiny />
                      <ExampleChip onClick={sendDirectMessage} text="En puerto" tiny />
                      <ExampleChip onClick={sendDirectMessage} text="Pendientes" tiny />
                      <ExampleChip onClick={sendDirectMessage} text="ayuda" tiny />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Pregunta algo... ej: MEDUWX474325, resumen, en puerto"
                      className="flex-1 bg-slate-800 border-slate-600 focus:border-primary text-white placeholder:text-slate-500"
                      disabled={isLoading}
                      data-testid="chatbot-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!inputValue.trim() || isLoading}
                      className="px-3"
                      data-testid="chatbot-send"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Consultas en tiempo real • Sin costo
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ChatBot;
